import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useMemo } from 'react';
import {
  measure,
  useAnimatedReaction,
  useAnimatedRef,
  useSharedValue,
} from 'react-native-reanimated';
import { useScreenSize } from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { isNotNil } from 'shared/src/utils';

const sectionVisibilityAtom = atom<Record<string, boolean>>({});

export const useIsSectionVisible = (key: string) => {
  const derivedAtom = useMemo(
    () => atom(get => isNotNil(get(sectionVisibilityAtom)[key])),
    [key],
  );
  return useAtomValue(derivedAtom);
};

export const useMarkSectionVisible = () => {
  const set = useSetAtom(sectionVisibilityAtom);
  return useCallback(
    (key: string) => {
      set(prev => {
        if (prev[key]) return prev;
        return { ...prev, [key]: true };
      });
    },
    [set],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 measure로 직접 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 반환된 animatedRef를 Animated.View에 연결하면,
 * 스크롤 시 해당 요소가 화면에 보이는 순간 visibility atom을 업데이트.
 */
export const useElementViewportTracking = (visibilityKey: string) => {
  const { scrollY } = useContext(ScrollContext);
  const { height: screenHeight } = useScreenSize();
  const animatedRef = useAnimatedRef();
  const isMarked = useSharedValue(false);
  const markVisible = useMarkSectionVisible();

  const onVisible = useCallback(
    () => markVisible(visibilityKey),
    [markVisible, visibilityKey],
  );

  useAnimatedReaction(
    () => scrollY?.value ?? 0,
    () => {
      if (isMarked.value) return;
      try {
        const measurement = measure(animatedRef);
        if (measurement && measurement.pageY < screenHeight) {
          isMarked.value = true;
          scheduleOnRN(onVisible);
        }
      } catch {
        // 컴포넌트가 언마운트된 후 measure 호출 시 네이티브 크래시 방지
      }
    },
    [screenHeight],
  );

  return animatedRef;
};
